import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  Pagination,
  Stack,
  TablePagination,
  TableSortLabel,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { selectBizOverviewAccountSummaryLoading } from "../../../../../../features/bizDevelopmentDashboardSlice";
import OverviewSkel from "../../../helpers/OverviewSkel";
import dayjs from "dayjs";
import {
  formatOverviewAccountSummaryValue,
  getOverviewAccountSummaryColumnLabel,
  OverviewAccountSummaryColumn,
} from "./overviewAccountSummaryTableHelpers";

interface Props {
  tableHeadings: OverviewAccountSummaryColumn[];
  rows: any[];
  selectedTimeframeLabel: string;
}

const OverviewAccountSummaryTable = ({
  tableHeadings,
  rows,
  selectedTimeframeLabel,
}: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [order, setOrder] = React.useState<"asc" | "desc">("desc");
  const [orderBy, setOrderBy] = React.useState("date");
  const isLoading = useSelector(selectBizOverviewAccountSummaryLoading);
  const headCells = tableHeadings;
  const page = parseInt(
    searchParams.get("pageOverviewAccountSummary") ?? "0",
    10
  );
  const rowsPerPage = parseInt(
    searchParams.get("rowsPerPageOverviewAccountSummary") ?? "10",
    10
  );

  const setParam = (updates: Record<string, string>) => {
    setSearchParams((prev) => {
      Object.entries(updates).forEach(([key, value]) => {
        prev.set(key, value);
      });
      return prev;
    });
  };

  const handleChangePage = (_: any, newPage: number) => {
    setParam({ pageOverviewAccountSummary: String(newPage) });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setParam({
      rowsPerPageOverviewAccountSummary: event.target.value,
      pageOverviewAccountSummary: "0",
    });
  };

  const parseSortValue = (value: any) => {
    if (typeof value === "string") {
      if (value.includes("%")) return parseFloat(value.replace("%", ""));
      if (value.includes("$")) return parseFloat(value.replace("$", ""));
      const parsedDate = dayjs(value);
      if (parsedDate.isValid()) return parsedDate.valueOf();
      return value.toLowerCase();
    }
    if (value === null || value === undefined) return "";
    return value;
  };

  const handleRequestSort = (property: string) => {
    const isAscending = orderBy === property && order === "asc";
    setOrder(isAscending ? "desc" : "asc");
    setOrderBy(property);
    setParam({ pageOverviewAccountSummary: "0" });
  };

  const getSortValue = (row: any, property: string) => {
    if (property === "date") {
      return row?.date ?? row?.month ?? row?.start_of_week ?? "";
    }
    return row?.[property];
  };

  const sortedRows = React.useMemo(() => {
    return [...(rows ?? [])].sort((a, b) => {
      const aValue = parseSortValue(getSortValue(a, orderBy));
      const bValue = parseSortValue(getSortValue(b, orderBy));
      if (aValue < bValue) return order === "asc" ? -1 : 1;
      if (aValue > bValue) return order === "asc" ? 1 : -1;
      return 0;
    });
  }, [rows, order, orderBy]);

  return (
    <>
      <TableContainer className="">
        <Table
          size="small"
          stickyHeader
          sx={{ minWidth: "1600px", whiteSpace: "nowrap" }}
        >
          <TableHead>
            <TableRow>
              {headCells.map((column) => (
                <TableCell key={column.key} align="left">
                  <TableSortLabel
                    active={orderBy === column.key}
                    direction={orderBy === column.key ? order : "asc"}
                    onClick={() => handleRequestSort(column.key)}
                  >
                    {getOverviewAccountSummaryColumnLabel(
                      column,
                      selectedTimeframeLabel
                    )}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {isLoading ? (
            <OverviewSkel
              columnCount={headCells.length}
              rowCount={rowsPerPage}
            />
          ) : (
            <TableBody>
              {rows?.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={headCells.length}
                    align="center"
                    sx={{ py: 6, color: "text.secondary" }}
                  >
                    No Data
                  </TableCell>
                </TableRow>
              ) : (
                sortedRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, rowIndex) => (
                    <React.Fragment key={row.cron_id ?? rowIndex}>
                      <TableRow
                        sx={{
                          "& > *": {
                            borderBottom:
                              "1px solid rgba(224, 224, 224, 1) !important",
                          },
                        }}
                      >
                        {headCells.map((column) => (
                          <TableCell
                            key={column.key}
                            align="left"
                            sx={{
                              color:
                                column.key === "profit" &&
                                Number(row[column.key]) > 20000
                                  ? "green"
                                  : undefined,
                            }}
                          >
                            {formatOverviewAccountSummaryValue(column, row)}
                          </TableCell>
                        ))}
                      </TableRow>
                    </React.Fragment>
                  ))
              )}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      {!isLoading && (
        <Stack
          spacing={1}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <TablePagination
            rowsPerPageOptions={[10, 25, 100]}
            component="div"
            count={sortedRows?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            classes={{
              displayedRows: "hidden-displayed-rows", // Apply the custom class
            }}
            sx={{
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-input": {
                marginLeft: "0px",
              },
              ".MuiTablePagination-toolbar": {
                paddingLeft: "16px",
              },
            }}
          />
          <Pagination
            count={Math.ceil(sortedRows?.length / rowsPerPage)}
            page={page + 1}
            showFirstButton
            showLastButton
            className="pagination-button"
            color="secondary"
            size="small"
            sx={{
              ".css-wjh20t-MuiPagination-ul": {
                justifyContent: "center",
              },
            }}
            onChange={(_, value) => handleChangePage(null, value - 1)}
          />
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={sortedRows?.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".css-16c50h-MuiInputBase-root-MuiTablePagination-select,.MuiTablePagination-selectLabel":
                {
                  display: "none",
                },
              ".MuiTablePagination-actions": {
                display: "none",
              },
              ".MuiTablePagination-toolbar": {
                paddingRight: "16px",
              },
            }}
          />
        </Stack>
      )}
    </>
  );
};

export default OverviewAccountSummaryTable;
